import React, { useEffect, useState } from "react";
import { Button } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { fetchOtpSmsLogDetail, resendOtpSmsLog } from "../actions";

const decodeBase64 = (value) => {
  try {
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      return window.atob(value);
    }
    if (typeof atob === "function") {
      return atob(value);
    }
    if (typeof Buffer !== "undefined") {
      return Buffer.from(value, "base64").toString("utf-8");
    }
  } catch (e) {
    return null;
  }
  return null;
};

const toNumericSmsLogId = (rawId) => {
  if (rawId === null || rawId === undefined) {
    return Number.NaN;
  }

  const value = String(rawId).trim();
  if (/^\d+$/.test(value)) {
    return Number.parseInt(value, 10);
  }

  // Relay global IDs are Base64-encoded as "TypeName:pk".
  const decoded = decodeBase64(value);
  if (!decoded) {
    return Number.NaN;
  }

  const maybePk = decoded.split(":").pop();
  if (!maybePk || !/^\d+$/.test(maybePk)) {
    return Number.NaN;
  }

  return Number.parseInt(maybePk, 10);
};

const styles = (theme) => ({
  root: {
    ...theme.typography.body1,
    fontSize: theme.typography.pxToRem(20),
  },
  title: {
    ...theme.typography.h5,
    marginBottom: theme.spacing(2),
  },
  backButton: {
    marginTop: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
  },
  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  keyCell: {
    width: "240px",
    borderBottom: "1px solid #f0f0f0",
    fontWeight: 600,
    verticalAlign: "top",
    padding: theme.spacing(1),
  },
  valueCell: {
    borderBottom: "1px solid #f0f0f0",
    padding: theme.spacing(1),
    wordBreak: "break-word",
  },
  apiResponse: {
    whiteSpace: "pre-wrap",
    margin: 0,
  },
});

const OtpDetailPage = ({ classes, match, history }) => {
  const id = match?.params?.id;
  const numericId = toNumericSmsLogId(id);

  const [smsLog, setSmsLog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  const loadDetail = async () => {
    if (!Number.isInteger(numericId)) {
      setError("Invalid SMS log id");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const detail = await fetchOtpSmsLogDetail(numericId);
      setSmsLog(detail);
      if (!detail) {
        setError("SMS log not found");
      }
    } catch (err) {
      setError(err?.message || "Failed to load SMS log detail");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id]);

  const onResend = async () => {
    setIsResending(true);
    setResendMessage(null);
    setError(null);
    try {
      const result = await resendOtpSmsLog(numericId);
      setResendMessage(result?.message || "Resend request processed");
      await loadDetail();
    } catch (err) {
      setError(err?.message || "Failed to resend SMS");
    } finally {
      setIsResending(false);
    }
  };

  if (isLoading) {
    return <div className={classes.root}>Loading SMS log detail...</div>;
  }

  if (error && !smsLog) {
    return <div className={classes.root}>Error loading SMS log detail: {error}</div>;
  }

  const showResendButton = smsLog?.status !== "Accepted";

  return (
    <div className={classes.root}>
      <h2 className={classes.title}>OTP SMS Log Detail</h2>
      {error && <div>Error: {error}</div>}
      {resendMessage && <div>{resendMessage}</div>}

      {smsLog && (
        <table className={`MuiTable-root ${classes.detailTable}`}>
          <tbody>
            <tr><td className={classes.keyCell}>id</td><td className={classes.valueCell}>{smsLog.id ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>uuid</td><td className={classes.valueCell}>{smsLog.uuid ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>insureeId</td><td className={classes.valueCell}>{smsLog.insureeId ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>insureeChfId</td><td className={classes.valueCell}>{smsLog.insureeChfId ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>claimUuid</td><td className={classes.valueCell}>{smsLog.claimUuid ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>claimCode</td><td className={classes.valueCell}>{smsLog.claimCode ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>phone</td><td className={classes.valueCell}>{smsLog.phone ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>otpCode</td><td className={classes.valueCell}>{smsLog.otpCode ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>message</td><td className={classes.valueCell}>{smsLog.message ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>messageId</td><td className={classes.valueCell}>{smsLog.messageId ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>clientReference</td><td className={classes.valueCell}>{smsLog.clientReference ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>scheduledAt</td><td className={classes.valueCell}>{smsLog.scheduledAt ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>createdAt</td><td className={classes.valueCell}>{smsLog.createdAt ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>messageCount</td><td className={classes.valueCell}>{smsLog.messageCount ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>smsFrom</td><td className={classes.valueCell}>{smsLog.smsFrom ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>network</td><td className={classes.valueCell}>{smsLog.network ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>smsType</td><td className={classes.valueCell}>{smsLog.smsType ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>status</td><td className={classes.valueCell}>{smsLog.status ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>isTest</td><td className={classes.valueCell}>{smsLog.isTest ? "true" : "false"}</td></tr>
            <tr><td className={classes.keyCell}>encryptMessage</td><td className={classes.valueCell}>{smsLog.encryptMessage ? "true" : "false"}</td></tr>
            <tr><td className={classes.keyCell}>encryptKey</td><td className={classes.valueCell}>{smsLog.encryptKey ?? "-"}</td></tr>
            <tr>
              <td className={classes.keyCell}>apiResponse</td>
              <td className={classes.valueCell}>
                <pre className={classes.apiResponse}>{smsLog.apiResponse ? JSON.stringify(smsLog.apiResponse, null, 2) : "-"}</pre>
              </td>
            </tr>
            <tr><td className={classes.keyCell}>errorMessage</td><td className={classes.valueCell}>{smsLog.errorMessage ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>isSent</td><td className={classes.valueCell}>{smsLog.isSent ? "true" : "false"}</td></tr>
            <tr><td className={classes.keyCell}>createdOn</td><td className={classes.valueCell}>{smsLog.createdOn ?? "-"}</td></tr>
            <tr><td className={classes.keyCell}>updatedOn</td><td className={classes.valueCell}>{smsLog.updatedOn ?? "-"}</td></tr>
          </tbody>
        </table>
      )}

      <div className={classes.actions}>
        {showResendButton && (
          <Button color="primary" variant="contained" onClick={onResend} disabled={isResending || !smsLog}>
            {isResending ? "Resending..." : "Resend"}
          </Button>
        )}
        <Button
          className={classes.backButton}
          color="primary"
          variant="outlined"
          onClick={() => {
            if (history?.push) {
              history.push("/otp/list");
            } else {
              window.location.href = `${process.env.PUBLIC_URL || ""}/otp/list`;
            }
          }}
        >
          Back to list
        </Button>
      </div>
    </div>
  );
};

export default withStyles(styles)(OtpDetailPage);
